/**
 * What a report ASKS FOR. The one thing that crosses the wire.
 *
 * NO SQL EVER LEAVES THE CLIENT. A tenant composes a specification and the server compiles it --
 * identifiers resolved from the semantic layer, operators taken from the tables below, values bound
 * as parameters, realm and visibility injected server-side. So there is no string to sanitise, and
 * the tenant boundary is a property of the compiler rather than of what somebody typed.
 *
 * THIS FILE IS THE CONTRACT, and `cres_t5/service/report_query.py` is the other half of it. The two
 * are hand-mirrored today. If they drift, the compiler refuses -- which is the safe direction, but
 * a generated contract is the eventual answer; see project_codegen_over_cqrs_direction.
 *
 * WHAT A GOOD ANALYTICAL TOOL CAN DO, WRITTEN DOWN. QuickSight, Tableau and Power BI converge on a
 * small vocabulary -- shelves of dimensions and measures, aggregations, date truncation, calculated
 * fields, table calculations, level-of-detail expressions, top-N, parameters. The whole vocabulary
 * is NAMED here even where the compiler does not implement it yet, and every entry carries the
 * release it lands in. That is deliberate:
 *
 *   - the UI can offer a control and grey it with a reason, instead of the feature being invisible
 *     and the tool looking less capable than it is going to be
 *   - the compiler can REFUSE PRECISELY -- "PERCENTILE is not compiled yet" rather than a generic
 *     failure, which is the difference between a roadmap and a bug
 *   - nobody has to rediscover what the category actually contains when they pick up phase 2
 *
 * The house rule from rule_sql applies to all of it: a compiler that guesses is worse than no
 * compiler, because the guess is invisible in the result. Anything not implemented REFUSES.
 */

/** Which release a vocabulary entry lands in. `v1` compiles today; the rest refuse, by name. */
export type Phase = "v1" | "next" | "later";

export type VocabularyEntry = {
    /** The wire value. Stable: it is stored inside saved reports. */
    id: string;
    /** What the builder shows. */
    label: string;
    phase: Phase;
    /** Why it is not available yet, shown on the greyed control. */
    note?: string;
};

// ---------------------------------------------------------------------------------------------
// Fields
// ---------------------------------------------------------------------------------------------

/**
 * A field is a DIMENSION or a MEASURE, which is the distinction every one of these tools makes and
 * the reason their builders can be direct-manipulation at all: a dimension slices, a measure
 * aggregates, and which shelf something may be dropped on follows from that.
 *
 * WE DO NOT HAVE TO GUESS IT. A form field carries `dataTypeRevisionUSID` -- a governed t5 Data
 * Type, pinned to a revision -- so the role comes from the semantic layer rather than from sniffing
 * values, which is where these tools get it wrong (an ID column summed because it happens to be a
 * number).
 */
export type FieldRole = "dimension" | "measure";

/** How a field is addressed. Never a column name, and never anything the user types. */
export type FieldRef = {
    /** The dataset it belongs to -- an object type's projection, e.g. "Work Instruction". */
    dataset: string;
    /** The form field's `code`, or a projected column of the revision itself ("status", "revision"). */
    field: string;
    /** For a repeating group (a `control: "Table"` field), the column within it. */
    within?: string;
};

// ---------------------------------------------------------------------------------------------
// Aggregation
// ---------------------------------------------------------------------------------------------

export const AGGREGATIONS: VocabularyEntry[] = [
    {id: "count", label: "Count", phase: "v1"},
    {id: "countDistinct", label: "Count distinct", phase: "v1"},
    {id: "sum", label: "Sum", phase: "v1"},
    {id: "avg", label: "Average", phase: "v1"},
    {id: "min", label: "Minimum", phase: "v1"},
    {id: "max", label: "Maximum", phase: "v1"},
    // Postgres has these outright; they wait only because a percentile needs a UI for its argument.
    {id: "median", label: "Median", phase: "next", note: "Not compiled yet"},
    {id: "percentile", label: "Percentile", phase: "next", note: "Needs a percentile argument"},
    {id: "stdev", label: "Standard deviation", phase: "next", note: "Not compiled yet"},
    {id: "variance", label: "Variance", phase: "next", note: "Not compiled yet"},
];

/**
 * Date truncation and numeric binning -- what these tools call the "date part" menu and histogram
 * bins. A dimension is rarely useful raw: nobody groups by timestamp, they group by month.
 */
export const GRAINS: VocabularyEntry[] = [
    {id: "year", label: "Year", phase: "v1"},
    {id: "quarter", label: "Quarter", phase: "v1"},
    {id: "month", label: "Month", phase: "v1"},
    {id: "week", label: "Week", phase: "v1"},
    {id: "day", label: "Day", phase: "v1"},
    {id: "numericBin", label: "Number range", phase: "next", note: "Needs a bin width"},
];

// ---------------------------------------------------------------------------------------------
// Filters
// ---------------------------------------------------------------------------------------------

/**
 * DELIBERATELY THE RULE VOCABULARY, not a second one. A quality rule already says "is one of",
 * "is greater than", "is blank" and the evaluator and `rule_sql` already agree on what each means
 * against untidy data. Inventing a parallel set for reports would be two answers to one question,
 * and they would disagree exactly where the data is messy -- which is where somebody is looking.
 */
export const FILTER_OPERATORS: VocabularyEntry[] = [
    {id: "equals", label: "is", phase: "v1"},
    {id: "notEquals", label: "is not", phase: "v1"},
    {id: "in", label: "is one of", phase: "v1"},
    {id: "notIn", label: "is not one of", phase: "v1"},
    {id: "contains", label: "contains", phase: "v1"},
    {id: "startsWith", label: "starts with", phase: "v1"},
    {id: "greaterThan", label: "is greater than", phase: "v1"},
    {id: "lessThan", label: "is less than", phase: "v1"},
    {id: "between", label: "is between", phase: "v1"},
    {id: "isBlank", label: "is blank", phase: "v1"},
    {id: "isNotBlank", label: "is not blank", phase: "v1"},
    // "Last 30 days" is a filter whose meaning depends on when it is RUN, which is exactly why it
    // must be a named operator rather than two dates the builder resolves once and saves.
    {id: "relativeDate", label: "is in the last…", phase: "v1"},
    // Top N is a filter in these tools and a window function underneath, which is why it is not v1.
    {id: "topN", label: "is in the top…", phase: "v1"},
];

export type Filter = {
    of: FieldRef;
    operator: string;
    /** relativeDate only: the period. A name the server looks up, never something it interpolates. */
    unit?: string;
    /** Bound as a parameter. Never interpolated, whatever it contains. */
    value?: unknown;
    values?: unknown[];
    /** True for a filter on an aggregate -- HAVING rather than WHERE. */
    afterGrouping?: boolean;
};

// ---------------------------------------------------------------------------------------------
// The spec
// ---------------------------------------------------------------------------------------------

// ---------------------------------------------------------------------------------------------
// Calculated fields
// ---------------------------------------------------------------------------------------------

/**
 * A calculation is a TREE, and there is no node that carries SQL.
 *
 * THIS IS THE ONE PLACE THE WHOLE DESIGN COULD BE UNDONE, which is why the shape is stated here
 * rather than left to whatever the builder happens to send. Everywhere else is safe because the
 * browser supplies no SQL -- and a calculated field is exactly where somebody would be tempted to
 * let it, because accepting a formula string is so much less work than this. The moment such a
 * string reaches the statement, the allow-list, the dispatch tables and the bound parameters are
 * all decoration.
 *
 * Every leaf is either a FIELD, resolved server-side through the same catalogue as any other
 * column, or a LITERAL NUMBER, bound as a parameter. Operators and functions are names the server
 * looks up in a table and refuses if absent. `cres_t5/service/report_query.py` is the half that
 * enforces all of it; this half only has to avoid offering what that one will refuse.
 */
export type Expression =
    | {kind: "field"; field: string}
    | {kind: "literal"; value: number}
    | {kind: "operator"; operator: string; left: Expression; right: Expression}
    | {kind: "function"; function: string; arguments: Expression[]};

export const EXPRESSION_OPERATORS: VocabularyEntry[] = [
    {id: "add", label: "+", phase: "v1"},
    {id: "subtract", label: "−", phase: "v1"},
    {id: "multiply", label: "×", phase: "v1"},
    // The server wraps the denominator in NULLIF, so a single zero row is a blank cell rather than
    // a failed statement.
    {id: "divide", label: "÷", phase: "v1"},
];

export const EXPRESSION_FUNCTIONS: VocabularyEntry[] = [
    {id: "round", label: "Round", phase: "v1"},
    {id: "abs", label: "Absolute value", phase: "v1"},
    {id: "ceiling", label: "Round up", phase: "v1"},
    {id: "floor", label: "Round down", phase: "v1"},
    {id: "coalesce", label: "Or else", phase: "v1"},
];

/** Mirrors the server. A tree is an input, so it is bounded in both directions. */
export const MAX_EXPRESSION_DEPTH = 8;
export const MAX_EXPRESSION_NODES = 60;

export type Calculation = {label: string; expression: Expression};

/**
 * A window over the rows the report already produced.
 *
 * NOT AN AGGREGATION. An aggregation collapses records into a group; a table calculation looks
 * across the GROUPS — a running total down the months, each department's share of the whole.
 *
 * TWO OF THEM NEED AN ORDER and refuse without one, because a running total down whatever order the
 * database happened to return is a number that disagrees with the rows beside it — and whose last
 * row is not the total.
 */
export const TABLE_CALCULATIONS: (VocabularyEntry & {needsSort?: boolean})[] = [
    {id: "runningTotal", label: "Running total", phase: "v1", needsSort: true},
    {id: "percentOfTotal", label: "% of total", phase: "v1"},
    {id: "rank", label: "Rank", phase: "v1"},
    {id: "difference", label: "Change from previous", phase: "v1", needsSort: true},
    {id: "movingAverage", label: "Moving average", phase: "next", note: "Needs a window size"},
];

/** The periods a relative date filter understands. */
export const RELATIVE_UNITS: VocabularyEntry[] = [
    {id: "day", label: "days", phase: "v1"},
    {id: "week", label: "weeks", phase: "v1"},
    {id: "month", label: "months", phase: "v1"},
    {id: "year", label: "years", phase: "v1"},
];

/**
 * A value a reader supplies when the report is run, rather than one written into it.
 *
 * DECLARED BY THE REPORT FIRST. A filter may reference a parameter only if the report declares it,
 * and the server refuses both a filter naming an undeclared one and a VALUE supplied for one. That
 * declaration is the allow-list — and it is what lets a saved report be re-run with different
 * values without being edited.
 */
export type Parameter = {name: string; label: string; default?: unknown};

export type Column = {
    /** A plain field. Absent on a calculated column -- a column is one or the other, never both. */
    of?: FieldRef;
    /** Arithmetic over fields. Always a measure: it is a number per row, summarized like any other. */
    calculation?: Calculation;
    role: FieldRole;
    /** Measures only. Omitted on a dimension. */
    aggregation?: string;
    /** Dimensions only: the grain to group at. */
    grain?: string;
    /** A window over the finished rows -- a running total, a share, a rank. */
    tableCalculation?: string;
    /** Dimensions the calculation is scoped to: each row against ITS GROUP rather than the whole.
     *  This is level-of-detail in the form people reach for -- a site's share of its region. Each
     *  must be a dimension the report already groups by. */
    within?: string[];
    /** What the column is headed. Cosmetic; never reaches SQL. */
    label?: string;
};

export type Sort = {of: FieldRef; direction: "asc" | "desc"; aggregation?: string};

/**
 * WHAT ONE ROW OF THE RESULT IS -- the question the whole shelf vocabulary turns on.
 *
 * A dimension normally does two things at once: it becomes a column AND it decides what one row
 * means. "detail" separates them. In a listing a dimension is only a column, nothing is combined,
 * and a measure has nothing to summarize over -- so measures, calculations, totals-filters and
 * top-N are all REFUSED rather than given a nearby meaning.
 *
 * WHY IT IS A PROPERTY OF THE REPORT AND NOT OF A SHELF. It changes what every shelf means at
 * once. Somebody who could set it per field would be describing two reports.
 *
 * ABSENT MEANS "summary", which is what every report saved before this shape existed said.
 */
export const SHAPES: VocabularyEntry[] = [
    {id: "summary", label: "One row per group of records", phase: "v1"},
    {id: "detail", label: "One row per record", phase: "v1"},
];

export type ReportQuery = {
    /** Pins the shape of this document, so a saved report outlives a change to it. */
    version: 1;
    dataset: string;
    /** What one row IS. Absent means "summary" -- see SHAPES. */
    shape?: "summary" | "detail";
    columns: Column[];
    filters?: Filter[];
    /** Values a reader supplies. The values themselves travel BESIDE the specification. */
    parameters?: Parameter[];
    sort?: Sort[];
    /** Server-capped regardless of what is asked for. */
    limit?: number;
};

/**
 * NOT IN THE SPEC, AND THAT IS THE POINT.
 *
 * `realm`, the persona, and the visibility mode are absent by design -- not optional, absent. They
 * are injected by the compiler from the session. A spec that could carry them, even ignored, is one
 * refactor away from being trusted, and the failure is silent and cross-tenant.
 *
 * Anything added to ReportQuery should be checked against that: could a tenant use this field to
 * widen what they can see? If yes it belongs on the server side of the boundary.
 */
export const SERVER_INJECTED = ["realm", "persona", "visibilityMode", "visibilityValue"] as const;

// ---------------------------------------------------------------------------------------------
// Declared, not yet built
// ---------------------------------------------------------------------------------------------

/**
 * The rest of what those tools do, named so the gap is legible rather than discovered.
 *
 * CALCULATED FIELDS ARE THE ONE TO GET RIGHT. Every tool has them and everybody wants them, and
 * they are the single place this design can be undone: the moment a calculation is a FORMULA STRING
 * that reaches SQL, no amount of care elsewhere matters. It has to be an expression tree over a
 * whitelisted function set, compiled by the same compiler, refusing the same way. Deferred because
 * it deserves its own design, not because it is optional.
 */
export const CAPABILITIES: VocabularyEntry[] = [
    {id: "aggregations", label: "Aggregate measures", phase: "v1"},
    {id: "dateGrain", label: "Group dates by part", phase: "v1"},
    {id: "filters", label: "Filter rows", phase: "v1"},
    {id: "havingFilters", label: "Filter on a total", phase: "v1"},
    {id: "sortLimit", label: "Sort and limit", phase: "v1"},
    {id: "detailRows", label: "List records instead of summarizing", phase: "v1"},
    {id: "calculatedFields", label: "Calculated fields", phase: "v1",
     note: "An expression tree over a fixed function set -- never a formula string"},
    {id: "tableCalculations", label: "Running total, % of total, rank", phase: "v1"},
    {id: "parameters", label: "Parameters a reader can change", phase: "v1"},
    // MEASURED, NOT ESTIMATED: raw.OBJECT_REFERENCE holds zero rows on prod and no form control
    // produces a reference, so there is no relationship data to join ON, no cardinality to declare,
    // and nothing to test a join against. Building one would mean writing an engine whose first
    // execution is against a customer's data -- and whose characteristic failure, fan-out, silently
    // multiplies measures rather than erroring.
    {id: "joins", label: "Combine two datasets", phase: "later",
     note: "Nothing links one record to another yet"},
    // The FIXED case -- a row against a total computed at a coarser grain -- is `within` above and
    // is built. INCLUDE and EXCLUDE, which compute at a grain the report does not show at all, are
    // not.
    {id: "levelOfDetail", label: "Totals at a coarser grain", phase: "v1"},
    {id: "levelOfDetailFiner", label: "Totals at a grain the report does not show", phase: "later",
     note: "Tableau INCLUDE/EXCLUDE"},
    {id: "totals", label: "Totals and subtotals", phase: "later"},
    {id: "drill", label: "Drill down a hierarchy", phase: "later",
     note: "Needs a form answer bound to a reference list, which the projection does not record"},
];

/** Everything a phase ships, for the builder to enable and the compiler to accept. */
export function available(entries: VocabularyEntry[], phase: Phase = "v1"): VocabularyEntry[] {
    return entries.filter(one => one.phase === phase);
}

/** Why a control is greyed, or null when it is not. */
export function unavailableReason(entries: VocabularyEntry[], id: string): string | null {
    const entry = entries.find(one => one.id === id);
    if (!entry) return "Not a known option";
    if (entry.phase === "v1") return null;
    return entry.note ?? "Not built yet";
}
